import React, { useCallback, useContext, useEffect, useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePickerConfigProps, DatePickerConfigValue } from './types';
import { toasterMsg } from '../../Toaster/Toaster';
import LibraryTitleInput from '../library-title-input';
import CustomTooltip from '../../Tooltip/CustomTooltip';
import { NewCheckbox } from '../../PIM/common/NewCheckbox';
import { NewInput } from '../../PIM/common/NewInput';
import FieldSetupPanelContext from './field-setup-panel.context';
import moment from 'moment';
import CmPanelTabs from '../cm-panel-tabs';
import { NewDatePicker } from '../../PIM/common/NewDatePicker';

const TOAST_DANGER = 'danger';

/**
 * DatePickerConfig component for configuring date picker form controls in the system canvas.
 * Allows users to set up date picker properties, required field settings, date range functionality,
 * and minimum/maximum date constraints.
 * @param value - Current date picker configuration value
 * @param onChange - Callback function called when configuration changes
 * @param className - Optional CSS class name for styling
 * @param disabled - Whether the configuration controls should be disabled
 * @param showLibraryTitle - Whether to show the library title input section
 */
const DatePickerConfig: React.FC<DatePickerConfigProps> = ({
  value,
  onChange,
  className = '',
  disabled = false,
  showLibraryTitle = true,
  width,
  onWidthChange,
  widthError,
  hideTitle = false,
}) => {
  const { t } = useTranslation('custom-module-system-canvas');
  const setupPanel = useContext(FieldSetupPanelContext);
  const isInSetupCard = setupPanel !== null;
  const showCardOptions = setupPanel === 'options';
  const titlePlaceholder = t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER');
  const showWidthRow = onWidthChange != null;
  const [localValue, setLocalValue] = useState<DatePickerConfigValue>(value);
  const [libraryTitle, setLibraryTitle] = useState<string>('');

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const updateTitle = useCallback(
    (next: string) => {
      setLocalValue((prev) => {
        const nextVal = { ...prev, title: next };
        onChange(nextVal);
        return nextVal;
      });
    },
    [onChange],
  );

  const toggleRequired = useCallback(() => {
    setLocalValue((prev) => {
      const nextVal = { ...prev, required: !prev.required };
      onChange(nextVal);
      return nextVal;
    });
  }, [onChange]);

  const toggleRange = useCallback(() => {
    setLocalValue((prev) => {
      const nextRange = !prev.range;
      const nextVal = {
        ...prev,
        range: nextRange,
        ...(nextRange ? {} : { minDate: null, maxDate: null }),
      };
      onChange(nextVal);
      return nextVal;
    });
  }, [onChange]);

  const updateMinDate = useCallback(
    (next: string) => {
      setLocalValue((prev) => {
        if (prev.maxDate && next && new Date(next) > new Date(prev.maxDate)) {
          toasterMsg(t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DATEPICKER.MIN_DATE_ERROR'), TOAST_DANGER);
          return prev;
        }
        const nextVal = { ...prev, minDate: next || null };
        onChange(nextVal);
        return nextVal;
      });
    },
    [t, onChange],
  );

  const updateMaxDate = useCallback(
    (next: string) => {
      setLocalValue((prev) => {
        if (prev.minDate && next && new Date(next) < new Date(prev.minDate)) {
          toasterMsg(t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DATEPICKER.MAX_DATE_ERROR'), TOAST_DANGER);
          return prev;
        }
        const nextVal = { ...prev, maxDate: next || null };
        onChange(nextVal);
        return nextVal;
      });
    },
    [t, onChange],
  );

  const handleLibrarySave = useCallback(() => {
    setLibraryTitle('');
  }, []);

  const handleLibrarySelect = useCallback(
    (controlTypeData: any) => {
      const fetchedConfig = controlTypeData?.content?.content as DatePickerConfigValue | undefined;
      if (fetchedConfig) {
        setLocalValue(fetchedConfig);
        onChange(fetchedConfig);
      }
    },
    [onChange],
  );

  const titleBlock = (
    <div className="mb-2 d-flex align-items-center">
      <div className="flex-grow-1">
        <p className="m-0">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DATEPICKER.TITLE')}</p>
        <NewInput
          type="text"
          placeholder={titlePlaceholder}
          value={localValue.title}
          onChange={(e) => updateTitle(e.target.value)}
          disabled={disabled}
        />
      </div>
    </div>
  );

  const dateMode = localValue.dateMode ?? 'absolute';
  const dateModeTabs = [
    { key: 'absolute' as const, label: t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DATEPICKER.ABSOLUTE') },
    { key: 'range' as const, label: t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DATEPICKER.RANGE') },
  ];

  /**
   * Switches the Options tab between one default date (Absolute) and a default From / To (Range).
   * @param mode - The picked mode.
   */
  const updateDateMode = (mode: 'absolute' | 'range') => {
    const nextVal: DatePickerConfigValue = { ...localValue, dateMode: mode, defaultDate: null, defaultFrom: null, defaultTo: null };
    setLocalValue(nextVal);
    onChange(nextVal);
  };

  /**
   * Stores one of the default dates in the format the product date field reads.
   * @param key - Which default date changed.
   * @param date - The picked date, or null when cleared.
   */
  const updateDefaultDate = (key: DefaultDateKey, date: Date | null) => {
    const nextVal: DatePickerConfigValue = { ...localValue, [key]: date ? moment(date).format(DEFAULT_DATE_FORMAT) : null };
    setLocalValue(nextVal);
    onChange(nextVal);
  };

  /**
   * Renders one labelled default-date picker; From and To limit each other so the range stays valid.
   * @param key - Which default date the picker edits.
   * @param label - Label above the picker.
   * @returns The labelled picker.
   */
  const renderDefaultDate = (key: DefaultDateKey, label: string) => {
    const current = localValue[key];
    const fromDate = localValue.defaultFrom ? new Date(localValue.defaultFrom) : undefined;
    const toDate = localValue.defaultTo ? new Date(localValue.defaultTo) : undefined;
    return (
      <div className="cm-field-setup__field cm-date-options__field">
        <span className="cm-field-setup__field-label">{label}</span>
        <NewDatePicker
          dateFormat={PICKER_DATE_FORMAT}
          placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DATEPICKER.PLACEHOLDER')}
          value={current ? new Date(current) : null}
          onChange={(date) => updateDefaultDate(key, date)}
          disabled={disabled}
          {...(key === 'defaultTo' && fromDate ? { minDate: fromDate } : {})}
          {...(key === 'defaultFrom' && toDate ? { maxDate: toDate } : {})}
          autoApply
        />
      </div>
    );
  };

  return (
    <div className={`cm-maxw-520 ${className}`}>
      {showWidthRow && (
        <div className="mb-2">
          <label className="f-12 d-block">Width (%)</label>
          <NewInput
            type="text"
            placeholder="e.g. 25% or 150px"
            value={width ?? ''}
            onChange={(e) => onWidthChange(e.target.value.trim())}
            title="Field width when rendered (e.g. 25% or 150px)"
            invalid={!!widthError}
          />
          {widthError && <div className="invalid-feedback d-block">{widthError}</div>}
        </div>
      )}
      {showWidthRow && titleBlock}
      {showLibraryTitle && (
        <LibraryTitleInput
          value={libraryTitle}
          onChange={setLibraryTitle}
          onSave={handleLibrarySave}
          onSelect={handleLibrarySelect}
          controlType="date-picker"
          contentData={localValue}
          placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER_LIBRARY_TITLE')}
        />
      )}
      {!isInSetupCard && (
      <div className="mb-1 mt-2 d-flex align-items-center cm-checkbox">
        <label className="m-0 mr-1">{t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')}</label>
        {!localValue.title?.trim() ? (
          <CustomTooltip tooltipText={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_CHECKBOX_TOOLTIP')}>
            <NewCheckbox className="mb-1 mr-0" checked={!!localValue.required} onChange={toggleRequired} disabled={disabled || !localValue.title?.trim()} aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')} />
          </CustomTooltip>
        ) : (
          <NewCheckbox className="mb-1 mr-0" checked={!!localValue.required} onChange={toggleRequired} disabled={disabled} aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')} />
        )}
      </div>
      )}

      {!showWidthRow && !hideTitle && titleBlock}
      {showCardOptions && (
        <div className="cm-date-options">
          <CmPanelTabs
            className="cm-panel-tabs--compact"
            tabs={dateModeTabs}
            activeKey={dateMode}
            onChange={updateDateMode}
            ariaLabel={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DATEPICKER.TITLE')}
          />
          {dateMode === 'absolute' ? (
            renderDefaultDate('defaultDate', t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DATEPICKER.DEFAULT_DATE'))
          ) : (
            <>
              {renderDefaultDate('defaultFrom', t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DATEPICKER.DEFAULT_FROM'))}
              {renderDefaultDate('defaultTo', t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DATEPICKER.DEFAULT_TO'))}
            </>
          )}
        </div>
      )}
      {!isInSetupCard && (
      <div className="mb-1 d-flex align-items-center cm-checkbox">
        <label className="m-0 mr-1">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DATEPICKER.RANGE')}</label>
        <NewCheckbox className="mb-1 mr-1" checked={!!localValue.range} onChange={toggleRange} disabled={disabled} aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DATEPICKER.RANGE')} />
      </div>
      )}

      {!isInSetupCard && localValue.range && (
        <div className="d-flex align-items-center gap-2">
          <div className="flex-grow-1">
            <p className="m-0">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DATEPICKER.MIN')}</p>
            <NewInput
              type="date"
              max={localValue.maxDate ?? ''}
              value={localValue.minDate ?? ''}
              onChange={(e) => updateMinDate(e.target.value)}
            />
          </div>
          <div className="ml-2 flex-grow-1">
            <p className="m-0">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.DATEPICKER.MAX')}</p>
            <NewInput
              type="date"
              min={localValue.minDate ?? ''}
              value={localValue.maxDate ?? ''}
              onChange={(e) => updateMaxDate(e.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(DatePickerConfig);

type DefaultDateKey = 'defaultDate' | 'defaultFrom' | 'defaultTo';

const DEFAULT_DATE_FORMAT = 'DD-MMM-YYYY';

const PICKER_DATE_FORMAT = 'dd-MMM-yyyy';
